/**
 * The org's model catalogue, and which one the composer is set to.
 *
 * Keyed on the organisation with the token from a ref, like the other fetch
 * hooks here — the session guard rotates the token and re-fetching the
 * catalogue on every rotation would also risk moving the user's selection.
 *
 * **A failed fetch hides the picker rather than showing an empty one.** The
 * agent has its own default model; offering a list that could not be loaded
 * (403, network) invites picking nothing, and picking a model the plan does not
 * expose is a 400 on the next turn.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL, isConfigured } from "../lib/config";
import {
  activeContextWindow,
  getOrgModels,
  resolveModel,
  type CurrentModel,
  type ModelOption,
} from "../lib/models";

export function useModels(
  orgId: string | null,
  token: string | null,
): {
  models: ModelOption[];
  current: CurrentModel | null;
  select: (model: CurrentModel) => void;
  /** The selected model's context window, before any `usage` event arrives. */
  contextWindow: number | null;
} {
  const [models, setModels] = useState<ModelOption[]>([]);
  const [current, setCurrent] = useState<CurrentModel | null>(null);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!orgId || !isConfigured()) {
      setModels([]);
      setCurrent(null);
      return;
    }

    let cancelled = false;
    getOrgModels(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((options) => {
        if (cancelled) return;
        setModels(options);
        setCurrent((prev) => resolveModel(options, prev));
      })
      .catch(() => {
        if (cancelled) return;
        setModels([]);
        setCurrent(null);
      });

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const select = useCallback((model: CurrentModel) => setCurrent(model), []);

  return { models, current, select, contextWindow: activeContextWindow(models, current) };
}
